import React from 'react';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  FileText, 
  Download, 
  Link2, 
  MoreHorizontal,
  Ban,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileCheck,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from '@/utils/formatters';
import { usePurchaseInvoices } from '@/hooks/usePurchaseInvoices';
import type { PendingPurchaseInvoice } from '@/types/purchaseInvoice';

/**
 * R-NF.5 (D-FNF.3): prazo para ciência da emissão, em dias, ancorado em emission_date
 * (dhEmi — a pendência não guarda dhRecbto/data de autorização). Só sinaliza:
 * NUNCA desabilita o botão de manifestação (a SEFAZ é a autoridade, 596 chega por toast).
 */
const PRAZO_CIENCIA_DIAS = 10;
const PRAZO_BORDA_DIAS = 8;

/**
 * E-NF12.2: fornecedor em nome de arquivo — sem acentos, só [A-Za-z0-9_-], espaços → _,
 * caixa preservada, cortado em 40.
 */
const FORNECEDOR_MAX_CHARS = 40;
function sanitizarFornecedor(nome: string | null | undefined): string {
  return (nome ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .replace(/_+/g, '_')
    .slice(0, FORNECEDOR_MAX_CHARS)
    .replace(/^[_-]+|[_-]+$/g, '');
}

/**
 * E-NF12.2: {prefixo}_{numero}_{fornecedor}.{ext}. Sem número cai na chave de acesso; sem
 * fornecedor o segmento some; sem nada devolve null (o chamador usa o fallback dele).
 */
function nomeArquivoNota(
  invoice: PendingPurchaseInvoice,
  prefixo: 'NFe' | 'DANFE',
  ext: 'xml' | 'pdf',
): string | null {
  const numero =
    String(invoice.invoice_number ?? '').replace(/[^A-Za-z0-9]/g, '') ||
    String(invoice.access_key ?? '').replace(/\D/g, '');
  if (!numero) return null;
  const fornecedor = sanitizarFornecedor(invoice.emitente_razao_social);
  return [prefixo, numero, fornecedor].filter(Boolean).join('_') + `.${ext}`;
}

/**
 * R-NF.13: recusa de nota — eventos conclusivos do destinatário. A Edge exige justificativa
 * ≥ 15 caracteres para os dois; o teto de 255 é o limite do xJust da SEFAZ.
 */
type TipoRecusa = 'desconhecimento' | 'nao_realizada';
const JUSTIFICATIVA_MIN = 15;
const JUSTIFICATIVA_MAX = 255;
const RECUSA_TEXTOS: Record<TipoRecusa, { item: string; titulo: string; explicacao: string }> = {
  desconhecimento: {
    item: 'Desconhecer operação',
    titulo: 'Desconhecimento da Operação',
    explicacao:
      'Declara à SEFAZ que a empresa NÃO reconhece esta operação: não comprou, não encomendou e não recebeu nada deste emitente.',
  },
  nao_realizada: {
    item: 'Operação não realizada',
    titulo: 'Operação não Realizada',
    explicacao:
      'Declara à SEFAZ que a operação foi contratada, mas NÃO se concretizou (mercadoria recusada, devolvida na entrega ou pedido cancelado).',
  },
};

interface PurchaseInvoicesListProps {
  invoices: PendingPurchaseInvoice[];
  onGenerateClick: (invoice: PendingPurchaseInvoice) => void;
  onLinkClick: (invoice: PendingPurchaseInvoice) => void;
}

export function PurchaseInvoicesList({ 
  invoices, 
  onGenerateClick, 
  onLinkClick 
}: PurchaseInvoicesListProps) {
  const {
    ignoreInvoice,
    manifestInvoice,
    updateInvoiceStatus,
    downloadDfeXml,
    downloadDanfePdf,
  } = usePurchaseInvoices();
  // R-NF.13: dialog de recusa (um só, parametrizado pelo tipo)
  const [recusa, setRecusa] = React.useState<{ invoice: PendingPurchaseInvoice; tipo: TipoRecusa } | null>(null);
  const [justificativa, setJustificativa] = React.useState('');
  const [enviandoRecusa, setEnviandoRecusa] = React.useState(false);
  // R-NF.12: o render do DANFE leva alguns segundos — trava o botão da linha enquanto roda
  const [danfeLoadingId, setDanfeLoadingId] = React.useState<string | null>(null);

  const getStatusBadge = (invoice: PendingPurchaseInvoice) => {
    // Check SEFAZ status first
    if (invoice.sefaz_status === 'cancelada') {
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          CANCELADA
        </Badge>
      );
    }
    if (invoice.sefaz_status === 'denegada') {
      return (
        <Badge variant="destructive" className="gap-1">
          <Ban className="h-3 w-3" />
          DENEGADA
        </Badge>
      );
    }

    // Check internal status
    switch (invoice.status) {
      case 'pending':
        return getPendingBadge(invoice);
      case 'processing':
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50">
            Processando
          </Badge>
        );
      case 'processed':
        return (
          <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 gap-1">
            <CheckCircle className="h-3 w-3" />
            Processada
          </Badge>
        );
      case 'linked':
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 gap-1">
            <Link2 className="h-3 w-3" />
            Vinculada
          </Badge>
        );
      case 'ignored':
        // R-NF.13 (D-NF13.1): nota RECUSADA sai do fluxo como 'ignored', mas o rastro da
        // manifestação vence o rótulo "Ignorada". Ignorada à mão segue "Ignorada".
        if (
          invoice.manifestation_status === 'desconhecimento' ||
          invoice.manifestation_status === 'nao_realizada'
        ) {
          return getManifestationBadge(invoice);
        }
        return (
          <Badge variant="secondary" className="gap-1">
            Ignorada
          </Badge>
        );
      default:
        return null;
    }
  };

  /** Badge da manifestação registrada — fonte única de rótulos e cores (ramos pending e ignored). */
  const getManifestationBadge = (invoice: PendingPurchaseInvoice) => {
    if (!invoice.manifestation_status) return null;
    const labels: Record<string, string> = {
      ciencia: invoice.xml_storage_path ? 'Informada' : 'Informada · aguardando XML',
      confirmacao: 'Confirmada',
      desconhecimento: 'Desconhecida',
      nao_realizada: 'Não realizada',
    };
    const colors: Record<string, string> = {
      ciencia: 'text-blue-600 border-blue-300 bg-blue-50',
      confirmacao: 'text-green-600 border-green-300 bg-green-50',
      desconhecimento: 'text-orange-600 border-orange-300 bg-orange-50',
      nao_realizada: 'text-red-600 border-red-300 bg-red-50',
    };
    const aguardandoXml = invoice.manifestation_status === 'ciencia' && !invoice.xml_storage_path;
    return (
      <Badge variant="outline" className={`gap-1 ${colors[invoice.manifestation_status]}`}>
        {aguardandoXml && <Clock className="h-3 w-3" />}
        {labels[invoice.manifestation_status]}
      </Badge>
    );
  };

  /**
   * R-NF.5: badge ÚNICO do ramo pending — ordem manifestação → prazo.
   * (sefaz_status e status já foram julgados em getStatusBadge.)
   */
  const getPendingBadge = (invoice: PendingPurchaseInvoice) => {
    // 3) manifestação registrada
    if (invoice.manifestation_status) return getManifestationBadge(invoice);

    // 4) a informar — faixa de prazo sobre emission_date (só sinal, nunca bloqueio)
    const dias = invoice.emission_date
      ? differenceInDays(new Date(), new Date(invoice.emission_date))
      : null;
    if (dias !== null && dias > PRAZO_CIENCIA_DIAS) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          Expirada ({dias}d)
        </Badge>
      );
    }
    if (dias !== null && dias >= PRAZO_BORDA_DIAS) {
      return (
        <Badge variant="outline" className="gap-1 text-amber-700 border-amber-400 bg-amber-50">
          <Clock className="h-3 w-3" />
          A informar · {dias}d
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
        A informar
      </Badge>
    );
  };

  const formatSupplierName = (name: string | null) => {
    if (!name) return 'Fornecedor não identificado';
    // Truncate long names
    return name.length > 30 ? name.substring(0, 30) + '...' : name;
  };

  // Arco A / C2: XML completo vive no bucket privado dfe-documents (xml_storage_path).
  // Sem path = só resumo — o dropdown de download nem aparece; nada de manifestar por aqui.
  const handleDownloadXml = async (invoice: PendingPurchaseInvoice) => {
    if (!invoice.xml_storage_path) return;

    try {
      const xml = await downloadDfeXml(invoice.xml_storage_path);
      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeArquivoNota(invoice, 'NFe', 'xml') ?? `nfe_${invoice.access_key}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar o XML da nota', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  // R-NF.12: DANFE da nota de fornecedor, renderizado pelo motor fiscal a partir do XML do
  // bucket (Edge render-danfe), sem o logo do tenant. Mesma condição do "Baixar XML".
  const handleDownloadDanfe = async (invoice: PendingPurchaseInvoice) => {
    if (!invoice.xml_storage_path || danfeLoadingId) return;

    setDanfeLoadingId(invoice.id);
    try {
      const { blob, filename } = await downloadDanfePdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // E-NF12.2: nome legível composto aqui; o filename da Edge é o fallback
      a.download = nomeArquivoNota(invoice, 'DANFE', 'pdf') ?? filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar o DANFE da nota', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setDanfeLoadingId(null);
    }
  };

  // D-NF11.8 (revoga D-143.11): a ciência é automática no cron da dfe-sync; este botão é o
  // fallback manual. Ciência → SEFAZ libera o procNFe → dfe-sync grava o path.
  const handleCiencia = (invoice: PendingPurchaseInvoice) => {
    manifestInvoice({ invoiceId: invoice.id, manifestationType: 'ciencia' })
      .catch(() => { /* onError da mutation já toasta o motivo */ });
  };

  const podeGerar = (invoice: PendingPurchaseInvoice) => !!invoice.xml_storage_path;
  const precisaCiencia = (invoice: PendingPurchaseInvoice) =>
    !invoice.xml_storage_path && !invoice.manifestation_status;

  const isCancelledOrDenied = (invoice: PendingPurchaseInvoice) => {
    return invoice.sefaz_status === 'cancelada' || invoice.sefaz_status === 'denegada';
  };

  // Mesma condição que o item "Ignorar nota" sempre teve
  const podeIgnorar = (invoice: PendingPurchaseInvoice) =>
    invoice.status === 'pending' && !isCancelledOrDenied(invoice);

  // R-NF.13: recusa cabe em pendência não processada/vinculada (inclusive após a ciência);
  // nota cancelada/denegada fica de fora — evento sobre ela só rende rejeição da SEFAZ.
  const podeRecusar = (invoice: PendingPurchaseInvoice) =>
    (invoice.status === 'pending' || invoice.status === 'ignored') &&
    !invoice.linked_purchase_id &&
    !isCancelledOrDenied(invoice);

  const abrirRecusa = (invoice: PendingPurchaseInvoice, tipo: TipoRecusa) => {
    setJustificativa('');
    setRecusa({ invoice, tipo });
  };

  const fecharRecusa = () => {
    if (enviandoRecusa) return; // não fecha com o evento em voo
    setRecusa(null);
    setJustificativa('');
  };

  const justificativaOk = justificativa.trim().length >= JUSTIFICATIVA_MIN;

  const handleConfirmarRecusa = async () => {
    if (!recusa || !justificativaOk || enviandoRecusa) return;
    const { invoice, tipo } = recusa;

    setEnviandoRecusa(true);
    try {
      // 1) Evento jurídico na SEFAZ (toasts de sucesso/erro por tipo moram na mutation)
      await manifestInvoice({
        invoiceId: invoice.id,
        manifestationType: tipo,
        justification: justificativa.trim(),
      });
    } catch {
      setEnviandoRecusa(false);
      return; // onError da mutation já toastou o motivo; dialog segue aberto p/ nova tentativa
    }

    try {
      // 2) D-NF13.1: nota recusada sai do fluxo de lançamento (o badge preserva o rastro)
      if (invoice.status !== 'ignored') {
        await updateInvoiceStatus({ invoiceId: invoice.id, status: 'ignored' });
      }
    } catch (error) {
      toast.warning('Evento registrado na SEFAZ, mas a nota não saiu do fluxo de lançamento', {
        description: error instanceof Error ? error.message : undefined,
        duration: 8000,
      });
    } finally {
      setEnviandoRecusa(false);
      setRecusa(null);
      setJustificativa('');
    }
  };

  if (invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">Nenhuma nota encontrada</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Clique em "Atualizar lista" para buscar novas notas fiscais
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Emissão</TableHead>
            <TableHead>Fornecedor</TableHead>
            <TableHead className="w-[120px]">Série - NF</TableHead>
            <TableHead className="w-[120px] text-right">Valor (R$)</TableHead>
            <TableHead className="w-[200px]">Situação</TableHead>
            <TableHead className="w-[180px] text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow 
              key={invoice.id}
              className={isCancelledOrDenied(invoice) ? 'opacity-60' : ''}
            >
              <TableCell className="font-medium">
                {invoice.emission_date 
                  ? format(new Date(invoice.emission_date), 'dd/MM/yy', { locale: ptBR })
                  : '-'}
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-medium truncate max-w-[250px]">
                        {formatSupplierName(invoice.emitente_razao_social)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{invoice.emitente_razao_social}</p>
                      <p className="text-xs text-muted-foreground">
                        CNPJ: {invoice.emitente_cnpj}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <span className="text-xs text-muted-foreground">
                    {invoice.emitente_cnpj?.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                {invoice.invoice_series || '1'} - {invoice.invoice_number || '-'}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(invoice.total_value || 0)}
              </TableCell>
              <TableCell>
                {getStatusBadge(invoice)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {/* Only show action buttons for non-cancelled invoices */}
                  {!isCancelledOrDenied(invoice) && invoice.status === 'pending' && (
                    <>
                      {podeGerar(invoice) && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => onGenerateClick(invoice)}
                        >
                          Lançar
                        </Button>
                      )}
                      {precisaCiencia(invoice) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleCiencia(invoice)}
                            >
                              <FileCheck className="h-4 w-4 mr-1" />
                              Informar à Receita
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Registra a ciência da emissão na SEFAZ; o XML completo chega no próximo sync (até 1h)
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onLinkClick(invoice)}
                          >
                            <Link2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Vincular nota</TooltipContent>
                      </Tooltip>
                    </>
                  )}

                  {/* E-NF12.2 (Lc-6, caminho único): o download da nota vive só neste dropdown —
                      XML do bucket e DANFE (PDF) renderizado pelo motor fiscal (R-NF.12). */}
                  {invoice.xml_storage_path && (
                    <DropdownMenu>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Download className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent>Baixar</TooltipContent>
                      </Tooltip>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDownloadXml(invoice)}>
                          <Download className="h-4 w-4 mr-2" />
                          XML
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDownloadDanfe(invoice)}
                          disabled={danfeLoadingId === invoice.id}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          {danfeLoadingId === invoice.id ? 'Gerando DANFE...' : 'DANFE (PDF)'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {/* "Ver detalhes" (TODO vazio desde o nascimento) removido — a DANFE (R-NF.12)
                      cobre a consulta. Sem item aplicável, o menu "⋯" nem aparece. */}
                  {(podeIgnorar(invoice) || podeRecusar(invoice)) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {podeIgnorar(invoice) && (
                          <DropdownMenuItem
                            onClick={() => ignoreInvoice(invoice.id)}
                            className="text-muted-foreground"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Ignorar nota
                          </DropdownMenuItem>
                        )}
                        {/* R-NF.13: recusa à SEFAZ — evento jurídico, sempre via dialog de confirmação */}
                        {podeRecusar(invoice) && (
                          <>
                            {podeIgnorar(invoice) && <DropdownMenuSeparator />}
                            <DropdownMenuItem
                              onClick={() => abrirRecusa(invoice, 'desconhecimento')}
                              className="text-orange-600"
                            >
                              <AlertTriangle className="h-4 w-4 mr-2" />
                              {RECUSA_TEXTOS.desconhecimento.item}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => abrirRecusa(invoice, 'nao_realizada')}
                              className="text-red-600"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              {RECUSA_TEXTOS.nao_realizada.item}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* R-NF.13: confirmação da recusa — um dialog só, parametrizado pelo tipo */}
      <Dialog open={!!recusa} onOpenChange={(open) => { if (!open) fecharRecusa(); }}>
        <DialogContent className="sm:max-w-lg">
          {recusa && (
            <>
              <DialogHeader>
                <DialogTitle>{RECUSA_TEXTOS[recusa.tipo].titulo}</DialogTitle>
                <DialogDescription>
                  {formatSupplierName(recusa.invoice.emitente_razao_social)} · NF{' '}
                  {recusa.invoice.invoice_number || '-'} · {formatCurrency(recusa.invoice.total_value || 0)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                  <p className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Evento jurídico enviado à SEFAZ
                  </p>
                  <p className="mt-1">{RECUSA_TEXTOS[recusa.tipo].explicacao}</p>
                  <p className="mt-1">
                    Não é uma ação interna do sistema: o evento fica registrado na Receita em nome da
                    empresa e fica visível ao emitente da nota. Vale a última manifestação registrada.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recusa-justificativa">Justificativa *</Label>
                  <Textarea
                    id="recusa-justificativa"
                    value={justificativa}
                    onChange={(e) => setJustificativa(e.target.value.slice(0, JUSTIFICATIVA_MAX))}
                    maxLength={JUSTIFICATIVA_MAX}
                    rows={4}
                    placeholder="Descreva o motivo (vai no evento enviado à SEFAZ)"
                    disabled={enviandoRecusa}
                  />
                  <p className={`text-xs ${justificativaOk ? 'text-muted-foreground' : 'text-destructive'}`}>
                    {justificativa.trim().length}/{JUSTIFICATIVA_MIN} mín. · máx. {JUSTIFICATIVA_MAX}
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={fecharRecusa} disabled={enviandoRecusa}>
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmarRecusa}
                  disabled={!justificativaOk || enviandoRecusa}
                >
                  {enviandoRecusa ? 'Enviando...' : 'Enviar à SEFAZ'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
